// ============================================================================
// Line Official Account — Webhook Adapter.
//
// Implements the `WebhookAdapter` contract so the ingress pipeline can:
//   1. Verify the `x-line-signature` HMAC-SHA256(base64) against the raw
//      request body using the channel secret (from `env.LINE_CHANNEL_SECRET`
//      or a per-instance override if later wired).
//   2. Parse the Line webhook envelope (`events[]`) into canonical capability
//      events that downstream modules (chat, AI) consume.
//
// Canonical docs:
//   - https://developers.line.biz/en/reference/messaging-api/#webhooks
//   - https://developers.line.biz/en/reference/messaging-api/#webhook-event-objects
//
// Retry-stable event ids: Line stamps a `webhookEventId` on every event
// (v2017+). We prefer that. For older / missing ids we synthesise a stable id
// from `type + source.userId + message.id` WITHOUT the `timestamp` field —
// Line retries use the same logical event but re-stamp the timestamp, which
// would break dedupe.
// ============================================================================

import type {
  MessageAttachment,
  MessageReceivedEvent,
} from '../../capabilities/messaging'
import {
  hmac,
  timingSafeEqualBytes,
  base64ToBytes,
} from '../../services/webhook-verifier'
import type {
  ParsedWebhookEvent,
  WebhookAdapter,
  WebhookAdapterContext,
} from '../../services/webhook-adapters'

// ============================================================================
// Raw Line payload shape — minimal, `unknown`-tolerant.
// ============================================================================

interface LineWebhookPayload {
  destination?: string
  events?: ReadonlyArray<LineEvent>
}

interface LineEvent {
  type?: string
  /** Unique per-event id added in 2017. When present, authoritative dedupe key. */
  webhookEventId?: string
  /** Millisecond timestamp the event occurred. */
  timestamp?: number
  /** When event is resent, `isRedelivery` is true. */
  isRedelivery?: boolean
  /** Free reply token (valid 30s, one-shot). Only present on user-initiated events. */
  replyToken?: string
  source?: LineSource
  message?: LineMessage
  postback?: LinePostback
  /** Present on `follow`/`unfollow`/`join`/`leave`/`memberJoined`/`memberLeft`. */
  follow?: { isUnblocked?: boolean }
  /** Present on `beacon` events. */
  beacon?: { hwid?: string; type?: string; dm?: string }
}

interface LineSource {
  type?: string
  /** User id (prefix `U` + 32 hex). Present on type=user and on events inside groups when profile is shared. */
  userId?: string
  groupId?: string
  roomId?: string
}

interface LineMessage {
  id?: string
  /** Line uses `text`, `image`, `video`, `audio`, `file`, `location`, `sticker`, `imagemap`. */
  type?: string
  text?: string
  /** Present on image/video/audio/file events; Line hosts the content. */
  contentProvider?: { type?: string; originalContentUrl?: string; previewImageUrl?: string }
  /** sticker fields */
  stickerId?: string
  packageId?: string
  /** file metadata */
  fileName?: string
  fileSize?: number
  /** location fields */
  title?: string
  address?: string
  latitude?: number
  longitude?: number
}

interface LinePostback {
  data?: string
  params?: Record<string, string>
}

// ============================================================================
// verifyWebhook — HMAC-SHA256(base64) of raw body using channel secret
// ============================================================================

async function verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean> {
  const channelSecret = pickSecret(ctx.env)
  // Missing secret → fail closed. Never accept webhooks when the channel
  // secret is misconfigured; returning `false` is uniform with FB adapter.
  if (!channelSecret) return false

  const header = ctx.headers.get('x-line-signature')
  if (!header) return false

  return verifyLineSignature(ctx.rawBody, header, channelSecret)
}

/**
 * Public signature verifier. Exposed so tests + ad-hoc tooling can verify
 * signed bodies without going through the full adapter context.
 *
 * Returns `false` on malformed header / wrong secret / tampered body.
 * Never throws — any crypto error downgrades to `false` so callers can
 * treat the envelope as unsigned uniformly.
 */
export async function verifyLineSignature(
  rawBody: ArrayBuffer | Uint8Array,
  signatureHeader: string,
  channelSecret: string,
): Promise<boolean> {
  if (!signatureHeader || signatureHeader.length === 0) return false
  if (!channelSecret || channelSecret.length === 0) return false

  let candidateBytes: Uint8Array
  try {
    candidateBytes = base64ToBytes(signatureHeader.trim())
  } catch {
    return false
  }

  let expectedBytes: Uint8Array
  try {
    expectedBytes = await hmac(channelSecret, rawBody, 'SHA-256')
  } catch {
    return false
  }

  return timingSafeEqualBytes(candidateBytes, expectedBytes)
}

/** Pick LINE_CHANNEL_SECRET from the env bag, tolerating absence / non-string values. */
function pickSecret(env: Record<string, unknown>): string | null {
  const raw = env?.LINE_CHANNEL_SECRET
  if (typeof raw !== 'string' || raw.length === 0) return null
  return raw
}

// ============================================================================
// parseWebhook
// ============================================================================

async function parseWebhook(ctx: WebhookAdapterContext): Promise<ParsedWebhookEvent[]> {
  return parseLineWebhook(ctx.rawBody)
}

/**
 * Public parser — accepts raw bytes or a pre-decoded string for convenience.
 * Throws `SyntaxError` on malformed JSON so the ingress pipeline returns 400.
 */
export function parseLineWebhook(
  rawBody: ArrayBuffer | Uint8Array | string,
): ParsedWebhookEvent[] {
  let text: string
  if (typeof rawBody === 'string') {
    text = rawBody
  } else if (rawBody instanceof Uint8Array) {
    text = new TextDecoder().decode(rawBody)
  } else {
    text = new TextDecoder().decode(new Uint8Array(rawBody))
  }

  const payload = JSON.parse(text) as LineWebhookPayload
  const events = Array.isArray(payload?.events) ? payload.events : []
  const out: ParsedWebhookEvent[] = []

  for (const event of events) {
    const type = typeof event?.type === 'string' ? event.type : ''
    if (!type) continue

    const senderId = typeof event.source?.userId === 'string' ? event.source.userId : ''
    const timestamp = typeof event.timestamp === 'number' ? event.timestamp : 0
    const receivedAt = isoFromTimestamp(timestamp)
    const replyToken = typeof event.replyToken === 'string' ? event.replyToken : undefined

    // Build retry-stable eventId. Prefer Line's own `webhookEventId`. Fallback
    // id deliberately excludes `timestamp` — Line retries re-stamp it and
    // dedupe would fail. `message.id` is the best tiebreaker for user
    // messages; for other types (follow/postback) we fall back to a type-
    // scoped combination of known ids.
    const messageId = typeof event.message?.id === 'string' ? event.message.id : ''
    const eventId = buildEventId(event, type, senderId, messageId)

    // 1. Inbound user message
    if (type === 'message' && event.message) {
      if (!senderId || !messageId) continue

      const msg = event.message
      const attachments = toAttachments(msg)
      const text = typeof msg.text === 'string' ? msg.text : undefined

      const receivedPayload: MessageReceivedEvent = {
        capability: 'messaging',
        event: 'message.received',
        // `instanceId` + `companyId` are stamped by the consuming service.
        instanceId: '',
        companyId: '',
        receivedAt,
        providerMessageId: messageId,
        senderId,
        ...(text !== undefined ? { text } : {}),
        ...(attachments.length > 0 ? { attachments } : {}),
      }

      // Stash the replyToken on the event so the dispatcher / sender can
      // later decide between reply (free) vs push (charged). We attach it to
      // the raw ParsedWebhookEvent for downstream persistence, since the
      // canonical MessageReceivedEvent shape does not model it.
      out.push({
        eventId,
        eventType: 'message.received',
        capability: 'messaging',
        payload: replyToken ? { ...receivedPayload, replyToken } : receivedPayload,
      })
      continue
    }

    // 2. follow / unfollow → subscribed / unsubscribed
    if (type === 'follow' || type === 'unfollow') {
      if (!senderId) continue
      const eventType =
        type === 'follow' ? 'user.subscribed' : 'user.unsubscribed'
      out.push({
        eventId,
        eventType: `messaging.${eventType}`,
        // Not a canonical capability event in the messaging type union yet —
        // leave capability undefined; the ingress pipeline will persist it
        // without emitting on the typed bus.
        payload: {
          event: eventType,
          instanceId: '',
          companyId: '',
          senderId,
          receivedAt,
        },
      })
      continue
    }

    // 3. postback
    if (type === 'postback' && event.postback) {
      if (!senderId) continue
      const data = typeof event.postback.data === 'string' ? event.postback.data : ''
      out.push({
        eventId,
        eventType: 'messaging.postback.received',
        payload: {
          event: 'postback.received',
          instanceId: '',
          companyId: '',
          senderId,
          receivedAt,
          data,
          ...(event.postback.params ? { params: event.postback.params } : {}),
          ...(replyToken ? { replyToken } : {}),
        },
      })
      continue
    }

    // 4. beacon — captured for audit only, not emitted on the messaging bus.
    //    We still return a ParsedWebhookEvent so the ingress pipeline logs
    //    delivery; without a `capability` it will be skipped for emit.
    if (type === 'beacon') {
      out.push({
        eventId,
        eventType: 'messaging.beacon.detected',
        payload: {
          event: 'beacon.detected',
          instanceId: '',
          companyId: '',
          senderId,
          receivedAt,
          hwid: typeof event.beacon?.hwid === 'string' ? event.beacon.hwid : '',
        },
      })
      continue
    }

    // Any other shape (join, leave, memberJoined, memberLeft, accountLink,
    // things, videoPlayComplete, ...) is intentionally ignored until a
    // downstream consumer needs it.
  }

  return out
}

/**
 * Build a retry-stable event id. Line retries re-stamp the `timestamp` but
 * preserve `webhookEventId` and the logical content, so we MUST NOT include
 * `timestamp` in the fallback key.
 */
function buildEventId(
  event: LineEvent,
  type: string,
  senderId: string,
  messageId: string,
): string {
  if (typeof event.webhookEventId === 'string' && event.webhookEventId.length > 0) {
    return event.webhookEventId
  }
  // Synthetic fallback: `type:userId:message.id`. When there's no message id
  // (follow/unfollow/postback), substitute a type-appropriate anchor so two
  // different logical events don't collide on the same user.
  if (messageId.length > 0) {
    return `${type}:${senderId || 'no-user'}:${messageId}`
  }
  if (type === 'postback') {
    const data =
      typeof event.postback?.data === 'string' ? event.postback.data : 'no-data'
    return `postback:${encodeURIComponent(senderId || 'no-user')}:${encodeURIComponent(data)}`
  }
  if (type === 'beacon') {
    const hwid =
      typeof event.beacon?.hwid === 'string' ? event.beacon.hwid : 'no-hwid'
    return `beacon:${encodeURIComponent(senderId || 'no-user')}:${hwid}`
  }
  return `${type}:${encodeURIComponent(senderId || 'no-user')}:no-msg-id`
}

// ============================================================================
// Attachment mapping — Line message types → capability `MessageAttachment`
// ============================================================================

function toAttachments(msg: LineMessage): MessageAttachment[] {
  const out: MessageAttachment[] = []
  const type = typeof msg.type === 'string' ? msg.type : ''
  // Line messages are ONE type per message — no `attachments[]` array.
  switch (type) {
    case 'image': {
      const url = msg.contentProvider?.originalContentUrl
      if (typeof url === 'string' && url.length > 0) {
        out.push({ type: 'image', url })
      }
      break
    }
    case 'video': {
      const url = msg.contentProvider?.originalContentUrl
      if (typeof url === 'string' && url.length > 0) {
        out.push({ type: 'video', url })
      }
      break
    }
    case 'audio': {
      const url = msg.contentProvider?.originalContentUrl
      if (typeof url === 'string' && url.length > 0) {
        out.push({ type: 'audio', url })
      }
      break
    }
    case 'file': {
      // File messages have no public URL from `contentProvider`; callers must
      // fetch via `/v2/bot/message/{messageId}/content`. We still surface the
      // filename + a placeholder so the UI can label the attachment.
      const filename = typeof msg.fileName === 'string' ? msg.fileName : undefined
      out.push({ type: 'file', url: '', ...(filename ? { filename } : {}) })
      break
    }
    // Text + sticker + location + imagemap + template are not modelled as
    // binary attachments; they surface via `text` or specialized handling
    // layered on top of the adapter.
    default:
      break
  }
  return out
}

// ============================================================================
// Time helpers
// ============================================================================

function isoFromTimestamp(msOrZero: number): string {
  if (!Number.isFinite(msOrZero) || msOrZero <= 0) {
    return new Date(0).toISOString()
  }
  return new Date(msOrZero).toISOString()
}

// ============================================================================
// Adapter export
// ============================================================================

export const lineOaWebhookAdapter: WebhookAdapter = {
  verifyWebhook,
  parseWebhook,
}
